import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { getTestById } from '@/data/tests';
import {
  ArrowLeft,
  Clock,
  Droplets,
  FileText,
  CheckCircle2,
  Shield,
  Home,
  Beaker
} from 'lucide-react';

export default function TestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const test = getTestById(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!test) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex p-6 bg-muted rounded-2xl mb-4">
            <FileText className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Test Not Found
          </h2>
          <p className="text-muted-foreground mb-6">
            The test you're looking for doesn't exist.
          </p>
          <Button asChild>
            <Link to="/tests">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to All Tests
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const features = [
    {
      icon: Shield,
      title: 'NABL Certified',
      description: 'Ensuring highest accuracy'
    },
    {
      icon: Home,
      title: 'Home Collection',
      description: 'Free sample pickup'
    },
    {
      icon: Clock,
      title: 'Quick Reports',
      description: `Results in ${test.reportTime}`
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Breadcrumb */}
      <section className="py-6 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link
              to="/"
              className="hover:text-primary transition-smooth"
            >
              Home
            </Link>
            <span>/</span>
            <Link
              to="/tests"
              className="hover:text-primary transition-smooth"
            >
              Tests
            </Link>
            <span>/</span>
            <span className="text-foreground">{test.name}</span>
          </div>
        </div>
      </section>

      {/* Test Details */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <Card className="shadow-card-hover">
                  <CardContent className="pt-6">
                    {/* Test Header */}
                    <div className="mb-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                            {test.name}
                          </h1>
                          <p className="text-lg text-muted-foreground">
                            {test.shortDescription}
                          </p>
                        </div>
                        {test.popular && (
                          <Badge className="ml-4">Popular</Badge>
                        )}
                      </div>

                      <div className="inline-block px-4 py-1.5 bg-primary/10 rounded-lg">
                        <span className="text-sm font-medium text-primary">
                          {test.category}
                        </span>
                      </div>
                    </div>

                    {/* Test Details Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 p-6 bg-muted/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Clock className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-0.5">
                            Report Time
                          </div>
                          <div className="text-sm font-semibold text-foreground">
                            {test.reportTime}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Droplets className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-0.5">
                            Sample Type
                          </div>
                          <div className="text-sm font-semibold text-foreground">
                            {test.sample}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Beaker className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-0.5">
                            Test ID
                          </div>
                          <div className="text-sm font-semibold text-foreground">
                            #{test.id}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold text-foreground mb-4">
                        About This Test
                      </h2>
                      <p className="text-muted-foreground leading-relaxed">
                        {test.description}
                      </p>
                    </div>

                    {/* Key Features */}
                    <div>
                      <h2 className="text-xl font-semibold text-foreground mb-4">
                        Why Choose D-Lab?
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {features.map((feature, index) => {
                          const IconComponent = feature.icon;
                          return (
                            <div
                              key={index}
                              className="flex flex-col items-center text-center p-4 bg-muted/30 rounded-lg"
                            >
                              <div className="p-3 bg-primary/10 rounded-xl mb-3">
                                <IconComponent className="h-6 w-6 text-primary" />
                              </div>
                              <div className="text-sm font-semibold text-foreground mb-1">
                                {feature.title}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {feature.description}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <Card className="shadow-card sticky top-24">
                  <CardContent className="pt-6">
                    {/* Price */}
                    <div className="mb-6 pb-6 border-b border-border">
                      <div className="text-sm text-muted-foreground mb-2">
                        Test Price
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-primary">
                          {test.price}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          onwards
                        </span>
                      </div>
                    </div>

                    {/* Book Button */}
                    <WhatsAppButton
                      testName={test.name}
                      className="w-full mb-4"
                      size="lg"
                    />

                    <Button
                      asChild
                      variant="outline"
                      className="w-full"
                      size="lg"
                    >
                      <Link to="/tests">Browse All Tests</Link>
                    </Button>

                    {/* Additional Info */}
                    <div className="mt-6 pt-6 border-t border-border space-y-3">
                      <div className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">
                          Free home sample collection
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">
                          Digital reports via email
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">
                          NABL certified results
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
